<template>
    <ob1-cards
        data-test-id="reco-mobile-card"
        class="reco-mobile-card d-flex flex-column h-100"
        additional-classes="px-1 pt-2"
        :is-multiple="true"
        :with-composite-link="false"
    >
        <a
            :href="mobile.deviceSummary.detailsUrl"
            class="card-link-wrapper text-decoration-none text-dark d-block"
            :data-pdb-zid="dataPdbZid + index"
            :data-pdb-sc="'adv'"
            :data-pdb-idc="mobile.ean.value"
            :data-pdb-vi="dataPdbVi"
            :data-pdb-ta="mobile.advTracker"
            data-oevent-category="zone_recommandee_advise"
            :data-oevent-action="'bloc_' + index"
            :data-oevent-label="tagLabel(mobile.deviceSummary.name)"
        >
            <div class="d-flex flex-column h-100 justify-content-between">
                <ob1-tag
                    v-if="mobile.deviceSummary.banner"
                    class="banner"
                    :label="mobile.deviceSummary.banner"
                />
                <div class="d-flex flex-row">
                    <div v-if="mobile.deviceSummary.image" class="img-device me-1 my-1">
                        <div class="image-container">
                            <ob1-picture
                                class="device-picture"
                                :visual="mobile.deviceSummary.image"
                            />
                        </div>
                    </div>
                    <div class="infos pr-1">
                        <div class="mobile-infos">
                            <div>{{ mobile.deviceSummary.brand }}</div>
                            <h2 class="no-signature standard py-0 my-0 fw-normal">
                                <strong class="standard device-name-wrapper">
                                    {{ mobile.deviceSummary.name }}
                                </strong>
                            </h2>
                            <Rating
                                v-if="mobile.deviceSummary.reviewsStats"
                                :average="mobile.deviceSummary.reviewsStats.average"
                                :rating-results-height="1.010"
                                :font-size="0.875"
                            />
                        </div>
                        <div class="infos-price">
                            <device-price
                                :compact-price="true"
                                :is-cents-format="true"
                                :multiple-price="mobile.deviceSummary.multiplePrices"
                                :total-price-with-promo="mobile.deviceSummary.discountedPriceInCents"
                                :total-price-without-promo="getPriceWithoutPromo()"
                                :monthly-amount-in-cents="getMonthlyAmountInCents()"
                                :monthly-duration="getMonthlyDuration()"
                                :generic-label="getPaymentInfoLabel()"
                                :loan-rate-zero-label="getZeroRateLabel()"
                                :is-price-inversion-enabled="isPriceInversionEnabled()"
                                :advantage-accessibility="advantageAccessibility"
                            />
                        </div>
                    </div>
                </div>
                <div class="infos-advantage py-1">
                    <template v-if="mobile.deviceSummary.advantageValues">
                        <advantage-bloc
                            :advantage-messages="advantageMessages"
                            :discount="mobile.deviceSummary.advantageValues.discount"
                            :odr="mobile.deviceSummary.advantageValues.odr"
                            :bonus-recovery="mobile.deviceSummary.advantageValues.bonusRecovery"
                        />
                    </template>
                </div>
                <!--/* Vendeur */-->
                <div v-if="showSoldByInfos" class="infos-seller">
                    <sold-by-card
                        :title="sellerLabel"
                        :sold-by-orange="mobile.deviceSummary.firstParty"
                    />
                </div>
            </div>
        </a>
        <div class="d-flex align-items-center payment-solutions-details pt-1">
            <!--/* Das */-->
            <das-tooltip
                v-if="showDasTooltip(mobile)"
                :id="'tooltip-reco-' + index"
                :das="mobile.deviceSummary.dasAccessible"
                class="me-auto"
                :aria-label="dasAriaLabel(mobile.deviceSummary.brand, mobile.deviceSummary.name)"
            />
            <!--/* Energy-Label */-->
            <energy-label
                v-if="mobile.deviceSummary.energyLabel"
                :id="mobile.deviceSummary.seoId"
                class="energy-label-padding ms-auto"
                :energy-label="mobile.deviceSummary.energyLabel"
                :with-link="false"
                :with-custom-image-class="true"
                :with-popin="false"
                data-oevent-category="selecteur_produit"
                @energyLabelClick="handleEnergyLabelClick"
            />
            <!--/* FSM - 4xCB */-->
            <FundingDetailsLink
                v-if="showFundingDetailsLink(mobile)"
                class="ms-auto"
                :seo-id="mobile.deviceSummary.seoId"
                :text="fundingDetailsDto.linkText"
                :device-name="mobile.deviceSummary.brand + ' ' + mobile.deviceSummary.name"
                @fundingDetailsClick="handleFundingDetailsClick"
            />
        </div>
    </ob1-cards>
</template>

<script>

import AdvantageBloc from "../../common/fragments/AdvantageBloc.vue";
import FundingDetailsLink from "../../common/FundingDetailsLink.vue";
import DasTooltip from "../../common/DasTooltip.vue";
import Rating from "../../../common/components/ob1/Rating.vue";
import SoldByCard from "../../../common/components/custom/SoldByCard.vue";
import DevicePrice from "../../../common/components/ob1/DevicePrice.vue";
import EnergyLabel from "../../common/EnergyLabel.vue";

export default {
    name: "RecoMobile",
    components: {
        AdvantageBloc,
        EnergyLabel,
        DevicePrice,
        SoldByCard,
        Rating,
        DasTooltip,
        FundingDetailsLink
    },
    props: {
        mobile: {
            type: Object,
            required: true
        },
        index: {
            type: Number,
            required: true
        },
        //Pour le tracking des balises métadonnées
        dataPdbZid: {
            type: String,
            required: true
        },
        advantageMessages: {
            type: Object,
            required: true
        },
        fundingDetailsDto: {
            type: Object,
            required: true
        },
        enablePriceInversion: {
            type: Boolean,
            default: false
        },
        hasThirdPartiesInCart: {
            type: Boolean,
            default: false
        },
        showSoldByInfos: {
            type: Boolean,
            default: false
        }
    },
    computed: {
        dataPdbVi() {
            return this.mobile.deviceSummary.image !== null ? this.mobile.deviceSummary.image.src : ""
        },
        sellerLabel() {
            return 'Vendu et expédié par <b>' + this.mobile.deviceSummary.seller + '</b>';
        },
        advantageAccessibility() {
            const advantages = this.mobile.deviceSummary.advantageValues;
            if (!advantages) {
                return null;
            }
            const parts = [];
            if (advantages.discount) {
                parts.push("grâce à " + this.advantageMessages.discount.replace(
                    "{0}",
                    PriceFormatter.formatEurosWithSpace(advantages.discount / 100, false).toString()
                ));
            }
            return parts.length ? parts.join(', ') : null;
        }
    },
    methods: {
        getPriceWithoutPromo() {
            if (this.mobile.deviceSummary.discountedPriceInCents === this.mobile.deviceSummary.priceInCents) {
                return null;
            }
            return this.mobile.deviceSummary.priceInCents;
        },
        getMonthlyAmountInCents() {
            return this.hasThirdPartiesInCart ? null : this.mobile.deviceSummary.monthlyPayment?.monthlyAmountInCents;
        },
        getMonthlyDuration() {
            return this.hasThirdPartiesInCart ? null : this.mobile.deviceSummary.monthlyPayment?.duration;
        },
        getPaymentInfoLabel() {
            return this.hasThirdPartiesInCart ? null : this.mobile.deviceSummary.monthlyPayment?.genericLabel;
        },
        getZeroRateLabel() {
            if (this.hasThirdPartiesInCart) return null;
            const monthlyPaymentTaegRate = this.mobile.deviceSummary.monthlyPayment?.taegRate;
            const isEligibleForZeroRate = this.mobile.deviceSummary.eligibleForZeroRate;
            if (monthlyPaymentTaegRate === 0) {
                return this.mobile.deviceSummary.zeroRateLabel;
            }
            if (monthlyPaymentTaegRate == null && isEligibleForZeroRate) {
                return this.mobile.deviceSummary.zeroRateOfferLabel;
            }
            return null;
        },
        isPriceInversionEnabled() {
            return this.enablePriceInversion && !this.hasThirdPartiesInCart && !!this.mobile.deviceSummary.monthlyPayment?.monthlyAmountInCents;
        },
        showDasTooltip: function (mobile) {
            return mobile.deviceSummary.dasAccessible;
        },
        showFundingDetailsLink: function (mobile) {
            return mobile.deviceSummary.hasInstallmentPlan && !this.hasThirdPartiesInCart;
        },
        tagLabel(label) {
            return stripAccents(label).split(' ').join('_')
        },
        handleFundingDetailsClick(versionSeoId) {
            this.$emit('fundingDetailsClick', versionSeoId);
        },
        handleEnergyLabelClick(seoId) {
            this.$emit('energyLabelClick', seoId);
        },
        dasAriaLabel(brand, name) {
            return brand ? 'DAS ' + brand + ' ' + name : 'DAS ' + name;
        }
    }
}
</script>
<style lang="scss">
@import 'ob1_common';
@import 'ob1_variables';

//Fond blanc et pleine largeur pour rester cohérent avec DeviceCard (la carte est posée sur un conteneur bg-gray2)
.reco-mobile-card.ob1-card {
    width: 100%;
    background-color: #fff;

    .banner {
        position: absolute;
        top: 0;
        left: 0;
    }

    .device-picture img {
        width: 100%;
        height: 100%;
        object-fit: contain;
    }

    .image-container {
        position: relative;
        width: px-to-rem(127);
        height: px-to-rem(127);
        overflow: hidden;
    }

    .img-device {
        display: flex;
        align-items: center;
        flex-direction: column;
        width: px-to-rem(120);
        height: px-to-rem(120);
        position: relative;
    }
}

//Surcharge car avec l'option ob1-card-multiple pas de changement de bordure au hover
.reco-mobile-card.ob1-card.ob1-card-multiple:hover {
    border-color: #555 !important;
    box-shadow: .125rem .125rem #555 !important;
}
</style>
